import { type FC, useState } from 'react'

import { Close } from '@radix-ui/react-dialog'

import Checkbox from 'core/components/lib/Checkbox'
import { Clickable, Spinner } from 'core/components'
import Dialog, {
  DialogActions,
  DialogText,
} from 'core/components/lib/Dialog/Dialog'
import { Select } from 'core/components/lib/Select'
import { useActiveEnv } from 'core/stores/env'
import {
  useCompanyGetAll,
  useInternalUsersGet,
  useInternalUserRoleGet,
  useInternalUserRoleUpdate,
  useInternalUserRoleDelete,
} from 'core/types'
import { useGetMultipleCompanyRoles } from 'core/types/hooks/multipleHooks'
import { R, S } from 'core/helpers'
import { styled } from 'styled-components'
import Icon from 'core/components/Icon'

const ALWAYS_VISIBLE_ROLE_ALIASES = new Set([
  'borrower',
  'agent',
  'admin',
  'supervisor',
  'peach-support',
] as Array<string>)

type CompanyRole = {
  id?: string
  alias?: string | null
  companyId?: string
  permissions?: Array<{ action?: string }> | null
}

const isAlwaysVisibleRole = (role: CompanyRole): boolean =>
  (role.permissions ?? []).some(
    (p) => typeof p?.action === 'string' && p.action.startsWith('super-admin'),
  ) ||
  (typeof role.alias === 'string' && role.alias.startsWith('super-admin')) ||
  (typeof role.alias === 'string' &&
    ALWAYS_VISIBLE_ROLE_ALIASES.has(role.alias))

const Admin: FC = () => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [roleCheckOverrides, setRoleCheckOverrides] = useState<
    Record<string, boolean>
  >({})
  const [isAllDialogOpen, setAllDialogOpen] = useState(false)
  const [allDialogAction, setAllDialogAction] = useState<'add' | 'remove'>(
    'add',
  )
  const [expandedCompanyIds, setExpandedCompanyIds] = useState<
    Record<string, boolean>
  >({})

  const env = useActiveEnv()
  const { id, isStandard, companyId } = env || {}

  const envLabel = !env
    ? 'unknown'
    : isStandard && ['development', 'sandbox', 'production'].includes(id || '')
    ? id
    : 'custom'

  const { data: users, isLoading: isLoadingUsers } = useInternalUsersGet({
    companyId,
    query: { limit: 100, userType: 'agent' },
    options: { enabled: envLabel !== 'custom' && !!companyId },
  })

  const { data: companies, error: companiesError } = useCompanyGetAll({
    query: { excludeInactive: true, limit: 100 },
    options: { enabled: envLabel !== 'custom' },
  })

  const { data: roles } = useGetMultipleCompanyRoles(
    R.pipe(
      companies ?? [],
      R.flatMap((company) =>
        !company?.id || envLabel === 'custom'
          ? []
          : [
              {
                companyId: company.id,
                query: { limit: 100 },
                options: { enabled: envLabel !== 'custom' },
              },
            ],
      ),
    ),
  )

  const { data: userRoles, isLoading: isLoadingUserRoles } =
    useInternalUserRoleGet({
      companyId,
      userId: selectedUserId ?? undefined,
      query: { limit: 100 },
      options: {
        enabled: envLabel !== 'custom' && !!companyId && !!selectedUserId,
      },
    })

  const { mutateAsync: addRoleToUser } = useInternalUserRoleUpdate({
    companyId,
    userId: selectedUserId ?? undefined,
    roleId: undefined,
  })

  const { mutateAsync: removeRoleFromUser } = useInternalUserRoleDelete({
    companyId,
    userId: selectedUserId ?? undefined,
    roleId: undefined,
  })

  const alwaysVisibleRoleIds = R.pipe(
    roles as Array<CompanyRole>,
    R.filter(isAlwaysVisibleRole),
    R.flatMap((role) => (!role?.id ? [] : [role.id])),
  )

  const serverCheckedRoleIdSet = new Set(
    R.pipe(
      userRoles ?? [],
      R.flatMap((role) => (!role?.id ? [] : [role.id])),
    ),
  )

  const isRoleChecked = (roleId: string) =>
    roleCheckOverrides[roleId] ?? serverCheckedRoleIdSet.has(roleId)

  const areAllChecked =
    alwaysVisibleRoleIds.length > 0 &&
    alwaysVisibleRoleIds.every((roleId) => isRoleChecked(roleId))

  return envLabel === 'custom' ? (
    <Page>Admin page not available for custom environments</Page>
  ) : (
    <Page>
      <StyledSelect
        disabled={isLoadingUsers || !companyId}
        isLoading={isLoadingUsers}
        isSearchable
        onChange={(nextUserId) => {
          setSelectedUserId(typeof nextUserId === 'string' ? nextUserId : null)
          setRoleCheckOverrides({})
          setExpandedCompanyIds({})
        }}
        options={R.pipe(
          users ?? [],
          R.flatMap((user) =>
            !user?.id || user?.type !== 'agent'
              ? []
              : [
                  {
                    value: user.id,
                    label: `${
                      S.join(
                        [user.employee?.firstName, user.employee?.lastName],
                        ' ',
                      ) || user.userName
                    } - ${user.id}`,
                  },
                ],
          ),
        )}
        placeholder={isLoadingUsers ? 'Loading users...' : 'Select a user'}
        value={selectedUserId}
      />
      {selectedUserId ? (
        <CheckboxArea>
          {isLoadingUserRoles ? (
            <CheckboxOverlay>
              <OverlaySpinner>
                <Spinner />
              </OverlaySpinner>
            </CheckboxOverlay>
          ) : null}
          <Group>
            <Checkbox
              label='All'
              name='all-roles'
              onChange={(value) => {
                if (!selectedUserId) return

                setAllDialogAction(value === true ? 'add' : 'remove')
                setAllDialogOpen(true)
              }}
              value={areAllChecked}
            />
          </Group>
          <Dialog
            content={
              <>
                <DialogText>
                  Are you sure you want to{' '}
                  {allDialogAction === 'add' ? 'add' : 'remove'} all roles{' '}
                  {allDialogAction === 'add' ? 'to' : 'from'} this user? This
                  will not affect roles that are in the "More roles" group under
                  each company.
                </DialogText>
                <DialogActions>
                  <Close asChild>
                    <Clickable variant='ghost'>No</Clickable>
                  </Close>
                  <Clickable
                    onClick={async () => {
                      if (!selectedUserId) return

                      const visibleRolesToChange = R.pipe(
                        roles as Array<CompanyRole>,
                        R.filter(isAlwaysVisibleRole),
                        R.filter((role) => !!role?.id && !!role?.companyId),
                      )

                      setRoleCheckOverrides((current) => ({
                        ...current,
                        ...Object.fromEntries(
                          alwaysVisibleRoleIds.map((roleId) => [
                            roleId,
                            allDialogAction === 'add',
                          ]),
                        ),
                      }))

                      const promises =
                        allDialogAction === 'add'
                          ? R.pipe(
                              visibleRolesToChange,
                              R.flatMap((role) =>
                                isRoleChecked(role.id as string)
                                  ? []
                                  : [
                                      addRoleToUser(undefined, {
                                        companyId: role.companyId,
                                        userId: selectedUserId,
                                        roleId: role.id,
                                      }),
                                    ],
                              ),
                            )
                          : R.pipe(
                              visibleRolesToChange,
                              R.flatMap((role) =>
                                !isRoleChecked(role.id as string)
                                  ? []
                                  : [
                                      removeRoleFromUser(undefined, {
                                        companyId: role.companyId,
                                        userId: selectedUserId,
                                        roleId: role.id,
                                      }),
                                    ],
                              ),
                            )

                      try {
                        await Promise.all(promises)
                        setAllDialogOpen(false)
                      } catch {
                        setRoleCheckOverrides((current) =>
                          R.omit(current, alwaysVisibleRoleIds),
                        )
                      }
                    }}
                    variant='secondary'
                  >
                    Yes
                  </Clickable>
                </DialogActions>
              </>
            }
            isOpen={isAllDialogOpen}
            onOpenChange={setAllDialogOpen}
            title={
              allDialogAction === 'add' ? 'Add all roles?' : 'Remove all roles?'
            }
            width='small'
          />
          {R.pipe(
            companies ?? [],
            R.flatMap((company) =>
              !company?.id
                ? []
                : [
                    <Group key={company.id}>
                      <CompanyName>
                        {company.legalName ?? company.name ?? company.id}
                      </CompanyName>
                      {(() => {
                        const companyRoles = R.pipe(
                          roles as Array<CompanyRole>,
                          R.filter((role) => role.companyId === company.id),
                        )

                        const { visible, extra } = R.reduce(
                          companyRoles,
                          (acc, role) => {
                            const isAlwaysVisible = isAlwaysVisibleRole(role)

                            return isAlwaysVisible
                              ? { ...acc, visible: [...acc.visible, role] }
                              : { ...acc, extra: [...acc.extra, role] }
                          },
                          {
                            visible: [] as Array<CompanyRole>,
                            extra: [] as Array<CompanyRole>,
                          },
                        )

                        const isExpanded = Boolean(
                          expandedCompanyIds[company.id],
                        )

                        const roleCheckbox = (role: CompanyRole) =>
                          !role?.id ? null : (
                            <Checkbox
                              key={role.id}
                              label={role.alias ?? role.id}
                              name={role.id}
                              onChange={async (value) => {
                                if (!selectedUserId || !role.id) return

                                if (value === true) {
                                  setRoleCheckOverrides((current) => ({
                                    ...current,
                                    [role.id as string]: true,
                                  }))
                                  try {
                                    await addRoleToUser(undefined, {
                                      companyId: role.companyId,
                                      userId: selectedUserId,
                                      roleId: role.id,
                                    })
                                  } catch {
                                    setRoleCheckOverrides((current) =>
                                      R.omit(current, [role.id as string]),
                                    )
                                  }
                                } else {
                                  setRoleCheckOverrides((current) => ({
                                    ...current,
                                    [role.id as string]: false,
                                  }))
                                  try {
                                    await removeRoleFromUser(undefined, {
                                      companyId: role.companyId,
                                      userId: selectedUserId,
                                      roleId: role.id,
                                    })
                                  } catch {
                                    setRoleCheckOverrides((current) =>
                                      R.omit(current, [role.id as string]),
                                    )
                                  }
                                }
                              }}
                              value={isRoleChecked(role.id)}
                            />
                          )

                        return (
                          <>
                            <CheckboxGroup>
                              {R.pipe(visible, R.map(roleCheckbox), R.compact)}
                            </CheckboxGroup>
                            {extra.length > 0 ? (
                              <>
                                <MoreRolesToggle
                                  onClick={() => {
                                    setExpandedCompanyIds((current) => ({
                                      ...current,
                                      [company.id as string]: !isExpanded,
                                    }))
                                  }}
                                  type='button'
                                  variant='outline'
                                >
                                  {isExpanded ? 'Hide' : 'More roles'}
                                  <Icon
                                    name={
                                      isExpanded
                                        ? 'keyboard_arrow_up'
                                        : 'keyboard_arrow_down'
                                    }
                                  />
                                </MoreRolesToggle>
                                {isExpanded ? (
                                  <CheckboxGroup>
                                    {R.pipe(
                                      extra,
                                      R.map(roleCheckbox),
                                      R.compact,
                                    )}
                                  </CheckboxGroup>
                                ) : null}
                              </>
                            ) : null}
                          </>
                        )
                      })()}
                    </Group>,
                  ],
            ),
          )}
        </CheckboxArea>
      ) : null}
      {companiesError ? <pre>{String(companiesError)}</pre> : null}
    </Page>
  )
}

export default Admin

const Page = styled.div`
  padding: 24px;
`

const StyledSelect = styled(Select)`
  margin-bottom: 40px;
`

const Group = styled.div`
  margin-bottom: 16px;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 4px;
  padding: 16px;
`
const CompanyName = styled.div`
  margin-bottom: 16px;
  color: ${(p) => p.theme.text};
  font-size: 16px;
  font-weight: 600;
`

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MoreRolesToggle = styled(Clickable)`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  margin-top: 12px;
  margin-bottom: 12px;
  color: ${(p) => p.theme.primary};
`

const CheckboxArea = styled.div`
  position: relative;
`

const CheckboxOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: rgb(255 255 255 / 60%);
`

const OverlaySpinner = styled.div`
  display: flex;
  position: absolute;
  inset: 0;
  align-items: start;
  justify-content: center;

  > div {
    height: 400px;
  }
`
